/**
 * Contact list query-param builder — shared filter→param mapping for the
 * contacts grid (desktop) and card list (mobile) views.
 *
 * Both surfaces let staff filter warehouse contacts (RMA/sales billing or
 * shipping primaries, presence of address/email/phone, created/modified
 * date ranges) and scope the list to a customer, a site group, or both.
 * This module is the single place that turns that filter state into the
 * backend query params, so the two views can't drift on param names or
 * date serialization.
 *
 * Contains:
 * - buildContactQueryParams — filters + scope → backend query params
 */

/**
 * Build backend query params for the contacts list endpoint from UI filter
 * state and scope selection.
 *
 * @param {Object} filters - advanced filter state (is_primary_rma_billing,
 *   is_primary_rma_shipping, is_primary_sales_billing, is_primary_sales_shipping,
 *   has_address, has_email, has_phone, created_after, created_before,
 *   modified_after, modified_before — date fields are `Date` objects)
 * @param {Object} options
 * @param {number} [options.customerId]
 * @param {number} [options.siteGroupId]
 * @param {'all'|'customer'|'site_group'} [options.scopeFilter='all']
 * @param {string} [options.searchQuery='']
 * @returns {Object} query params to pass to fetchContactsAPI (merge with
 *   pagination/sort params at the call site)
 */
export const buildContactQueryParams = (
  filters = {},
  { customerId, siteGroupId, scopeFilter = 'all', searchQuery = '' } = {},
) => {
  const params = {};

  if (searchQuery) {
    params.text_search = searchQuery;
  }

  if (filters.is_primary_rma_billing) {
    params.primary_rma_billing = true;
  }
  if (filters.is_primary_rma_shipping) {
    params.primary_rma_shipping = true;
  }
  if (filters.is_primary_sales_billing) {
    params.primary_sales_billing = true;
  }
  if (filters.is_primary_sales_shipping) {
    params.primary_sales_shipping = true;
  }
  if (filters.has_address) {
    params.has_address = true;
  }
  if (filters.has_email) {
    params.has_email = true;
  }
  if (filters.has_phone) {
    params.has_phone = true;
  }
  if (filters.created_after) {
    params.created_after = filters.created_after.toISOString().split('T')[0];
  }
  if (filters.created_before) {
    params.created_before = filters.created_before.toISOString().split('T')[0];
  }
  if (filters.modified_after) {
    params.modified_after = filters.modified_after.toISOString().split('T')[0];
  }
  if (filters.modified_before) {
    params.modified_before = filters.modified_before.toISOString().split('T')[0];
  }

  // Apply scope-based filtering with backend parameters
  if (scopeFilter === 'site_group' && siteGroupId) {
    // Only site group contacts
    params.site_group = Number(siteGroupId);
  } else if (scopeFilter === 'customer' && customerId) {
    // Only customer-level contacts
    params.customer = Number(customerId);
  } else if (scopeFilter === 'all' && siteGroupId) {
    // Both customer and site group contacts
    params.site_group = Number(siteGroupId);
    params.include_customer_contacts = true;
  } else if (customerId) {
    // Default: customer contacts
    params.customer = Number(customerId);
  }

  return params;
};
